import type {
  HttpUserPrompt,
  PromptTextPart,
  PromptWandbRunPart,
} from '@wandb/weave/lib/WbAgent/promptParts';
import {type Descendant, Text as SlateText} from 'slate';

import type {ContentPart} from '../../agentChat/store';
import type {SelectedRun} from './WBAgentContextStore';

/**
 * Builds the frontend store parts for a user prompt from the composer's
 * Slate document. The runtime later derives the backend shape
 * (`HttpUserPrompt`) from these store parts.
 */
export function buildPromptPartsFromSlate(
  nodes: Descendant[],
  selectedRuns: readonly SelectedRun[],
  scope: {
    entityName: string;
    projectName: string;
  }
): ContentPart[] {
  const parts: ContentPart[] = [];

  for (const run of selectedRuns) {
    parts.push({
      type: 'wandb-run',
      entityName: scope.entityName,
      projectName: scope.projectName,
      runName: run.name,
      runDisplayName: run.displayName,
      // Composer always supplies a real display name from the run picker,
      // so mark these as resolved to match the shape produced by
      // `httpPromptToStoreContent` after the async resolver completes.
      runDisplayNameStatus: 'resolved',
    });
  }
  // Push empty text part to distinguish when user text message
  // (which can include in-line mentions) start. This is important for
  // properly rendering messages that start with in-line mentions.
  parts.push({type: 'text', text: '', status: 'complete'});

  walkSlate(nodes, chunk => {
    if (chunk.kind === 'text' && chunk.text.length > 0) {
      const last = parts[parts.length - 1];
      if (last?.type === 'text') {
        last.text += chunk.text;
      } else {
        parts.push({type: 'text', text: chunk.text, status: 'complete'});
      }
    } else if (chunk.kind === 'run-mention') {
      parts.push({
        type: 'wandb-run',
        // Capture the entity and project name from when the run was added,
        // ensuring it's accurate if the user navigates away before sending.
        entityName: chunk.entityName,
        projectName: chunk.projectName,
        runName: chunk.name,
        runDisplayName: chunk.displayName,
        runDisplayNameStatus: 'resolved',
      });
    }
  });
  // This strips the space that the Composer's Slate Editor automatically appends after a run mention
  const last = parts[parts.length - 1];
  if (last?.type === 'text' && last.text.trim() === '') {
    parts.pop();
  }

  return parts;
}

type SlateChunk =
  | {kind: 'text'; text: string}
  | {
      kind: 'run-mention';
      name: string;
      displayName: string;
      entityName: string;
      projectName: string;
    };

function walkSlate(
  nodes: Descendant[],
  emit: (chunk: SlateChunk) => void
): void {
  nodes.forEach((node, index) => {
    if (index > 0) {
      emit({kind: 'text', text: '\n'});
    }
    walkSlateInline(node, emit);
  });
}

function walkSlateInline(
  node: Descendant,
  emit: (chunk: SlateChunk) => void
): void {
  if (SlateText.isText(node)) {
    if (node.text.length > 0) {
      emit({kind: 'text', text: node.text});
    }
    return;
  }
  if (node.type === 'run-mention') {
    emit({
      kind: 'run-mention',
      name: node.name,
      displayName: node.displayName,
      entityName: node.entityName,
      projectName: node.projectName,
    });
    return;
  }
  if (node.children) {
    for (const child of node.children) {
      walkSlateInline(child, emit);
    }
  }
}

interface ShellResult {
  stdout?: string;
  stderr?: string;
  exit_code?: number;
}

export const isShellResult = (value: unknown): value is ShellResult => {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const result = value as Record<string, unknown>;
  return (
    ('stdout' in result || 'stderr' in result || 'exit_code' in result) &&
    (result.stdout === undefined || typeof result.stdout === 'string') &&
    (result.stderr === undefined || typeof result.stderr === 'string') &&
    (result.exit_code === undefined || typeof result.exit_code === 'number')
  );
};

export const extractPythonCodeBlock = (command: string): string | null => {
  const match = command.match(
    /^python\s+-\s+<<-?['"]?(\w+)['"]?\n([\s\S]*?)\n\1\s*$/
  );
  return match ? match[2] : null;
};

const PROMPT_CONTEXT_RE = /^<PromptContext>[\s\S]*?<\/PromptContext>\s*/;
const INLINE_RUN_ID_RE =
  /\{run_id: "[^"]*"(?:,\s*run_display_name: "([^"]*)")?\}/g;
const CONTEXT_RUN_RE =
  /\{run_id: "([^"]*)"(?:,\s*run_display_name: "([^"]*)")?\}/g;

export function encodeMention(name: string, displayName: string): string {
  return `{run_id: "${name}", run_display_name: "${displayName}"}`;
}

export function encodePromptContext(
  runs: ReadonlyArray<{name: string; displayName: string}>,
  userText: string
): string {
  if (runs.length === 0) {
    return userText;
  }
  const contextBlock = runs
    .map(r => encodeMention(r.name, r.displayName))
    .join('\n');
  return `<PromptContext> The following data is provided as supplementary to the user prompt\n${contextBlock}\n</PromptContext>\n${userText}`;
}

export function decodePromptForThreadTitle(raw: HttpUserPrompt): string {
  let text: string;
  if (typeof raw === 'string') {
    text = raw;
  } else {
    // First text part marks user content per buildPromptPartsFromSlate;
    // earlier parts are metadata (route context, attached run pills).
    const start = raw.findIndex(p => p.type === 'text');
    text = (start < 0 ? [] : raw.slice(start))
      .map(p => {
        if (p.type === 'text') {
          return (p as PromptTextPart).text;
        }
        if (p.type === 'wandb_run') {
          return `@${(p as PromptWandbRunPart).run_name}`;
        }
        return '';
      })
      .join(' ');
  }

  const cleaned = text
    .replace(PROMPT_CONTEXT_RE, '')
    .replace(INLINE_RUN_ID_RE, (_, displayName) =>
      displayName ? `@${displayName}` : ''
    )
    .replace(/  +/g, ' ')
    .trim();

  if (cleaned) {
    return cleaned;
  }
  // Fallback for historical threads that were created using the legacy API
  const contextMatch = text.match(PROMPT_CONTEXT_RE);
  if (contextMatch) {
    const contextNames: string[] = [];
    for (const m of contextMatch[0].matchAll(CONTEXT_RUN_RE)) {
      if (m[2]) {
        contextNames.push(m[2]);
      }
    }
    if (contextNames.length > 0) {
      return contextNames.map(name => `@${name}`).join(', ');
    }
  }
  return '';
}

export function decodePromptForMessageDisplay(raw: string): string {
  return raw
    .replace(PROMPT_CONTEXT_RE, '')
    .replace(INLINE_RUN_ID_RE, (_, displayName) =>
      displayName ? `\`${displayName}\`` : ''
    );
}
